import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onSelectKnowledgeBase", "selectedKnowledgeBaseId", "onAddKnowledgeBaseClick", "disabledKnowledgeBaseIds", "DisabledTooltipTitle"];
import { useCallback, useMemo } from 'react';
import { useFetchKnowledgeBases } from '../queries/KnowledgeBasesQuery';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { selectHasAccessToMultiKB, selectKnowledgeBaseLimit } from '../selectors/selectHasAccessToMultiKB';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import { getSettingUrl } from '../../routing/utils/externalUrls';
import { useAppSelector } from '../../utils/hooks';
import { useHasScope } from '../../auth/hooks/authHooks';
import { KNOWLEDGE_BASE_WRITE } from '../../constants/ScopesConstants';
import { Button } from 'foundations-components/transitional/components/Button';
import { Link } from 'foundations-components/transitional/components/Link';
import { Select } from 'foundations-components/transitional/components/Select';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
const KnowledgeBaseSelect = _ref => {
  let {
      onSelectKnowledgeBase,
      selectedKnowledgeBaseId,
      onAddKnowledgeBaseClick,
      disabledKnowledgeBaseIds = [],
      DisabledTooltipTitle
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    hasScope: hasKnowledgeBaseWrite
  } = useHasScope(KNOWLEDGE_BASE_WRITE);
  const {
    data: knowledgeBases
  } = useFetchKnowledgeBases();
  const hasAccessToMultiKB = useAppSelector(selectHasAccessToMultiKB);
  const knowledgeBaseLimit = useAppSelector(selectKnowledgeBaseLimit);
  const {
    openUpgradeModal
  } = useUpgradeModal('multiple-knowledge-base-creation', !hasAccessToMultiKB);
  const handleSelectKnowledgeBase = useCallback(({
    currentTarget: {
      value: newKnowledgeBaseId
    }
  }) => {
    onSelectKnowledgeBase(parseInt(newKnowledgeBaseId, 10));
  }, [onSelectKnowledgeBase]);
  const knowledgeBasesByTextAndValue = useMemo(() => {
    return knowledgeBases ? knowledgeBases.filter(kb => !kb.isConvertedSCPKnowledgeBase).map(({
      name,
      id
    }) => ({
      text: name,
      value: id,
      disabled: disabledKnowledgeBaseIds.includes(id)
    })) : undefined;
  }, [knowledgeBases, disabledKnowledgeBaseIds]);
  const hasUsedAllKnowledgeBases = !!knowledgeBases && !!knowledgeBaseLimit && knowledgeBases.length >= knowledgeBaseLimit;
  const tooltipText = !hasKnowledgeBaseWrite ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "knowledgeContentUI.shared.permissions"
  }) : hasAccessToMultiKB ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: "knowledgeContentUI.knowledgeBaseSelect.reachedKnowledgeBaseLimit_jsx",
    elements: {
      UILink: Link
    },
    options: {
      href: getSettingUrl(),
      external: true,
      style: {
        whiteSpace: 'nowrap'
      }
    }
  }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: "knowledgeContentUI.knowledgeBaseSelect.upgradeForMultipleKnowledgeBasesTooltip_jsx",
    elements: {
      UIButton: Button
    },
    options: {
      onClick: openUpgradeModal,
      use: 'link-on-dark'
    }
  });
  const ItemComponent = useCallback(({
    children,
    option,
    ['data-option-value']: dataOptionValue
  }) => /*#__PURE__*/_jsx(Tooltip, {
    disabled: !option.disabled,
    title: DisabledTooltipTitle && /*#__PURE__*/_jsx(DisabledTooltipTitle, {
      knowledgeBaseId: option.value
    }),
    placement: "left",
    children: /*#__PURE__*/_jsx("span", {
      "data-option-value": dataOptionValue,
      children: children
    })
  }), [DisabledTooltipTitle]);
  return /*#__PURE__*/_jsx(Select, Object.assign({
    "data-test-id": "knowledge-base-select"
  }, rest, {
    options: knowledgeBasesByTextAndValue,
    onChange: handleSelectKnowledgeBase,
    value: selectedKnowledgeBaseId,
    menuWidth: "auto",
    itemComponent: ItemComponent,
    dropdownFooter: /*#__PURE__*/_jsx(Tooltip, {
      title: tooltipText,
      disabled: !hasUsedAllKnowledgeBases && !!hasKnowledgeBaseWrite,
      placement: "right",
      children: /*#__PURE__*/_jsx(Button, {
        disabled: hasUsedAllKnowledgeBases || !hasKnowledgeBaseWrite,
        onClick: onAddKnowledgeBaseClick,
        use: "link",
        children: /*#__PURE__*/_jsx(Text, {
          textDecoration: "underline",
          typography: "heading-50",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "knowledgeContentUI.knowledgeBaseSelect.createFooter"
          })
        })
      })
    })
  }));
};
export default KnowledgeBaseSelect;